import { createAsyncThunk } from "@reduxjs/toolkit"

import { OrderQueryparams } from "../../../../core/app/slices/patients/patient.types"
import { QUERY_PARAMS_STORAGE_KEY_ORDER } from "../../../../modules/Patient/PatientList/patientTable/table.config"
import { IAlignerOrderPayload } from "../../../model/interface/AlignerOrder"
import { IShippingAddressTo } from "../../../model/interface/IShippingAddress"
import store from "../../store"
import { clinicalRequestBasicType } from "../clinical/clinical.types"

import {
  createAlternateShippingAddress,
  downloadUassistSummary,
  fetchShippingMethods,
  fetchUAssistOrders,
  getActiveOrders,
  getCustomPackagePricing,
  getMaterials,
  getOrdersByCase,
  getPackageDetails,
  getQuotationService,
  getShippingAddress,
  getStageDetails,
  placeAlignerOrder,
  placeManufacturingOrder,
  updateOrderStatus,
} from "./order.service"

export const updateOrder = createAsyncThunk(
  "orderService/updateOrderStatus",
  async ({
    caseId,
    type,
    upper,
    lower,
  }: {
    caseId: string
    type: string
    upper: string
    lower: string
  }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      const payload = {
        case_id: caseId,
        order_type: type,
      }
      payload["upper_type"] = upper || ""
      payload["lower_type"] = lower || ""
      return updateOrderStatus(orgId, payload)
    } catch (err) {
      return err
    }
  },
)

export const updateOrderPrint = createAsyncThunk(
  "orderService/updateOrderStatus",
  async ({
    caseId,
    type,
    upperStageRange,
    lowerStageRange,
    upperStageCount,
    lowerStageCount,
    upperRetainerStage,
    lowerRetainerStage,
    upperTemplateStage,
    lowerTemplateStage,
  }: {
    caseId: string
    type: string // "In Office Order" | "Direct Print Aligner"
    upperStageRange: string // "1,2,3,4"
    lowerStageRange: string // "1,2,3,4"
    upperStageCount: number
    lowerStageCount: number
    upperRetainerStage: string // "500,501"
    lowerRetainerStage: string // "500,501"
    upperTemplateStage: string // "400"
    lowerTemplateStage: string // "400"
  }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      const payload = {
        case_id: caseId,
        order_type: type,
        upper_stage_range: upperStageRange,
        lower_stage_range: lowerStageRange,
        upper_stage_count: upperStageCount,
        lower_stage_count: lowerStageCount,
        upper_retainer_stage: upperRetainerStage,
        lower_retainer_stage: lowerRetainerStage,
        upper_template_stage: upperTemplateStage,
        lower_template_stage: lowerTemplateStage,
      }
      return updateOrderStatus(orgId, payload)
    } catch (err) {
      return err
    }
  },
)

export const getOrdersByCaseId = createAsyncThunk(
  "orderService/getOrdersByCaseId",
  async (caseId: number, { rejectWithValue }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return getOrdersByCase(orgId, caseId)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getQuotation = createAsyncThunk(
  "orderService/getQuotation",
  async (
    { payload }: { payload: any; isQuoteUpdate?: boolean },
    { rejectWithValue },
  ) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return getQuotationService(orgId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const placeAlignerOrders = createAsyncThunk(
  "orderService/placeAlignerOrders",
  async (payload: IAlignerOrderPayload, { rejectWithValue }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return placeAlignerOrder(orgId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const placeManufacturingOrders = createAsyncThunk(
  "orderService/placeManufacturingOrders",
  async (
    {
      orderId,
      payload,
    }: {
      orderId: string
      payload: {
        sub_orders: number[]
        status: number
        txplan_name: string
        txplan_subname: string
      }
    },
    { rejectWithValue },
  ) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return placeManufacturingOrder(orgId, orderId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getShippingAddressByCase = createAsyncThunk(
  "orderService/getShippingAddressByCase",
  async () => {
    const orgId = store.getState()?.userService?.user?.current_orgId
    const userId = store.getState().userService.user.user_id
    try {
      return getShippingAddress(orgId, userId)
    } catch (err) {
      return err
    }
  },
)

export const getPackageDetail = createAsyncThunk(
  "orderService/getPackageDetail",
  async () => {
    const orgId = store.getState()?.userService?.user?.current_orgId
    try {
      return getPackageDetails(orgId)
    } catch (err) {
      return err
    }
  },
)

export const getStageDetailApi = createAsyncThunk(
  "clinicalService/getStageDetails",
  async (
    { patientId, caseId }: clinicalRequestBasicType,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState()?.userService?.user?.current_orgId
    try {
      return await getStageDetails({
        orgId,
        patientId,
        caseId,
      })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const createAlternateAddress = createAsyncThunk(
  "orderService/createAlternateAddress",
  async (payload: IShippingAddressTo, { rejectWithValue }) => {
    const orgId = store.getState().userService.user.current_orgId
    const userId = store.getState().userService.user.user_id
    try {
      return createAlternateShippingAddress(orgId, userId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getMaterialsAPI = createAsyncThunk(
  "clinicalService/getMaterial",
  async (
    { patientId, caseId }: clinicalRequestBasicType,
    { rejectWithValue, getState },
  ) => {
    const orgId = getState()?.userService?.user?.current_orgId
    try {
      return await getMaterials({
        orgId,
        patientId,
        caseId,
      })
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getActiveOrdersAPI = createAsyncThunk(
  "orderService/getActiveOrders",
  async (
    { orderQueryParams }: { orderQueryParams: OrderQueryparams },
    { rejectWithValue, getState },
  ) => {
    const orgId = getState()?.userService?.user?.current_orgId
    try {
      localStorage.setItem(
        QUERY_PARAMS_STORAGE_KEY_ORDER,
        JSON.stringify(orderQueryParams),
      )
      return await getActiveOrders(orgId, orderQueryParams)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getCustomPackagePrice = createAsyncThunk(
  "orderService/getCustomPackagePricing",
  async () => {
    const orgId = store.getState()?.userService?.user?.current_orgId
    try {
      return getCustomPackagePricing(orgId)
    } catch (err) {
      return err
    }
  },
)

export const getShippingMethods = createAsyncThunk(
  "orderService/getShippingMethods",
  async (payload: any, { rejectWithValue }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return fetchShippingMethods(orgId, payload)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const downloadUassistSummaryPdf = createAsyncThunk(
  "orderService/downloadUassistSummary",
  async (
    { orgId, orderId }: { orgId: string; orderId: string },
    { rejectWithValue },
  ) => {
    try {
      return await downloadUassistSummary(orgId, orderId)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const getUAssistOrders = createAsyncThunk(
  "orderService/getUAssistOrders",
  async (payload: any, { rejectWithValue }) => {
    const orgId = store.getState().userService.user.current_orgId
    try {
      return fetchUAssistOrders(orgId, payload.patientId, payload.caseId)
    } catch (err) {
      return err
    }
  },
)
